import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const TABLE_NAME = 'rd_cco';

const COLATE = [
  'CCA',
  'CCB',
];

const normalizeText = value =>
  String(value ?? '')
    .trim()
    .toUpperCase();

const normalizeValue = value =>
  String(value ?? '').trim();

const validDate = value => {
  const time = new Date(value).getTime();

  return Number.isFinite(time)
    ? time
    : null;
};

const formatDate = value => {
  const time = validDate(value);

  if (time === null) {
    return '—';
  }

  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Rome',
  }).format(new Date(time));
};

const formatDateTime = value => {
  const time = validDate(value);

  if (time === null) {
    return '—';
  }

  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Rome',
  }).format(new Date(time));
};

const daysSince = value => {
  const time = validDate(value);

  if (time === null) {
    return null;
  }

  return Math.max(
    0,
    Math.floor(
      (Date.now() - time) / 86400000
    )
  );
};

const getColataTheme = colata => {
  if (normalizeText(colata) === 'CCA') {
    return {
      text: 'text-blue-500',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      active: 'bg-blue-500 text-white',
      dot: 'bg-blue-500',
      rgb: [59, 130, 246],
    };
  }

  return {
    text: 'text-violet-500',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
    active: 'bg-violet-500 text-white',
    dot: 'bg-violet-500',
    rgb: [139, 92, 246],
  };
};

const getStatusTheme = status => {
  if (normalizeText(status) === 'PRONTO') {
    return {
      label: 'Pronto',
      text: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/25',
      dot: 'bg-emerald-500',
      icon: 'CheckCircle2',
      line: 'bg-emerald-500',
      pdfColor: [16, 185, 129],
    };
  }

  return {
    label: 'In riparazione',
    text: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/25',
    dot: 'bg-orange-500',
    icon: 'Wrench',
    line: 'bg-orange-500',
    pdfColor: [249, 115, 22],
  };
};

/*
 * Per una macchina PRONTO si usa data_rientro se disponibile.
 * Se data_rientro è vuota, si usa data_intervento.
 *
 * Per IN RIPARAZIONE viene usata data_intervento:
 * rappresenta la data dello SMONTATO.
 */
const getReferenceDate = (
  item,
  status
) => {
  if (
    normalizeText(status) === 'PRONTO' &&
    item.data_rientro
  ) {
    return item.data_rientro;
  }

  return item.data_intervento;
};

const getDateLabel = status =>
  normalizeText(status) === 'PRONTO'
    ? 'Rientrata il'
    : 'Smontata il';

const getPdfDateLabel = status =>
  normalizeText(status) === 'PRONTO'
    ? 'DATA RIENTRO'
    : 'DATA SMONT.';

const MachinePill = ({
  item,
  status,
  onOpen,
}) => {
  const state = getStatusTheme(status);
  const colata = getColataTheme(item.colata);

  const referenceDate = getReferenceDate(
    item,
    status
  );

  const days = daysSince(referenceDate);

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={`w-full text-left flex flex-col gap-3 p-4 rounded-2xl border transition-all duration-200 hover:scale-[1.01] hover:shadow-lg hover:shadow-black/[0.04] ${state.bg} ${state.border}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${state.bg} ${state.text}`}
          >
            <Icon name="Settings2" size={15} />
          </div>

          <div className="min-w-0">
            <span className="block text-[9px] font-black uppercase tracking-widest text-muted-foreground/45">
              Macchina di scorta
            </span>

            <span className="block text-sm sm:text-base font-black uppercase tracking-tight text-foreground truncate">
              {item.n_macchina || '—'}
            </span>
          </div>
        </div>

        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl border shrink-0 ${state.bg} ${state.border}`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${state.dot}`}
          />

          <span
            className={`text-[9px] font-black uppercase tracking-widest ${state.text}`}
          >
            {state.label}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/20">
        <div className="min-w-0">
          <span className="block text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">
        
          </span>

          <span
            className={`mt-1 inline-flex items-center gap-1.5 text-[12px] font-black uppercase tracking-widest ${colata.text}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${colata.dot}`}
            />

            {item.colata || '—'}
          </span>
        </div>

        <div className="min-w-0">
          <span className="block text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">
          
          </span>

          <span className="block mt-1 text-[12px] font-black uppercase tracking-widest text-foreground/70 truncate">
            {item.linea || '—'}
          </span>
        </div>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <span
            className={`flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest ${state.text}`}
          >
            <span
              className={`w-2 h-2 rounded-full ${state.dot}`}
            />

            {getDateLabel(status)}
          </span>

          <span className="block mt-1 text-[9px] font-bold text-muted-foreground/45 truncate">
      
          </span>
        </div>

        <div className="flex flex-col items-end gap-1 shrink-0">

           <span className="text-[11px] font-bold tabular-nums text-muted-foreground/45 whitespace-nowrap">
            {formatDate(referenceDate)}
          </span>
          <span
            className={`flex items-center gap-1 text-[11px] font-black whitespace-nowrap ${state.text}`}
          >
            <Icon name="CalendarDays" size={11} />

            {days === 0
              ? 'Oggi'
              : days === null
                ? '—'
                : `${days} gg fa`}
          </span>

        
        </div>
      </div>

      <div className="pt-2 border-t border-border/20 flex justify-center">
        <span className="text-[11px] font-black uppercase tracking-widest text-muted-foreground/45">
          Apri dettagli
        </span>
      </div>
    </button>
  );
};

const StockDetailModal = ({
  item,
  status,
  onClose,
}) => {
  if (!item) {
    return null;
  }

  const state = getStatusTheme(status);

  const referenceDate = getReferenceDate(
    item,
    status
  );

  const days = daysSince(referenceDate);

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Chiudi dettagli macchina"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md rounded-[2rem] bg-card border border-border/60 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className={`h-1 ${state.line}`} />

        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="text-[10px] font-black uppercase tracking-[.25em] text-primary/60">
                Dettaglio macchina di scorta
              </span>

              <h3 className="mt-1 text-xl sm:text-2xl font-black uppercase tracking-tight truncate">
                {item.n_macchina || 'N/D'}
              </h3>

              <span className="text-[11px] font-bold text-muted-foreground/50">
                {item.colata || '—'} · {item.linea || '—'}
              </span>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-10 h-10 shrink-0 rounded-2xl bg-muted/50 hover:bg-red-500/10 border border-border/40 text-muted-foreground hover:text-red-500 flex items-center justify-center transition-all"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          <div
            className={`mt-6 p-4 rounded-2xl border ${state.bg} ${state.border}`}
          >
            <div className="flex items-center justify-between gap-3">
              <span
                className={`flex items-center gap-2 text-[11px] font-black uppercase tracking-widest ${state.text}`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${state.dot}`}
                />

                {state.label}
              </span>

              <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/45">
               
              </span>
            </div>
          </div>

          <div
            className={`mt-3 p-4 rounded-2xl border ${state.bg} ${state.border}`}
          >
            <div className="flex items-center justify-between gap-3">
              <span
                className={`flex items-center gap-2 text-[11px] font-black uppercase tracking-widest ${state.text}`}
              >
                <Icon name="CalendarDays" size={14} />

                {getDateLabel(status)}
              </span>

              <span
                className={`text-lg font-black ${state.text}`}
              >
                {days === 0
                  ? 'Oggi'
                  : days === null
                    ? '—'
                    : `${days} giorni fa`}
              </span>
            </div>

            <span className="block mt-2 text-[10px] font-bold text-muted-foreground/55">
              {formatDateTime(referenceDate)}
            </span>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            {[
              ['Colata', item.colata],
              ['Linea', item.linea],
           
            ].map(([label, value]) => (
              <div
                key={label}
                className="p-3 rounded-xl bg-muted/20 border border-border/25"
              >
                <span className="block text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">
                  {label}
                </span>

                <span className="block mt-1 text-[12px] font-black text-foreground/75 truncate">
                  {value || '---'}
                </span>
              </div>
            ))}
          </div>

          {item.note && (
            <div className="mt-4 p-4 rounded-2xl bg-muted/20 border border-border/25">
              <span className="block text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">
                Note
              </span>

              <p className="mt-1 text-xs font-bold leading-relaxed text-foreground/70">
                {item.note}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({
  title,
  icon,
  count,
  items,
  status,
  onOpen,
}) => {
  const state = getStatusTheme(status);

  return (
    <div
      className={`flex flex-col rounded-3xl border overflow-hidden ${state.border} ${state.bg}`}
    >
      <div
        className={`px-5 sm:px-6 py-5 flex items-center justify-between border-b ${state.border} ${state.bg}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${state.bg} ${state.text}`}
          >
            <Icon name={icon} size={17} />
          </div>

          <div className="min-w-0">
            <h3
              className={`text-sm font-black uppercase tracking-tight ${state.text}`}
            >
              {title}
            </h3>

            <p className="text-[10px] font-bold text-muted-foreground/50 uppercase tracking-widest mt-0.5">
              {count === 0
                ? 'Nessuna macchina'
                : count === 1
                  ? '1 macchina'
                  : `${count} macchine`}
            </p>
          </div>
        </div>

        <div
          className={`min-w-[2.5rem] h-10 rounded-2xl flex items-center justify-center px-3 text-lg font-black tabular-nums text-white shadow-lg ${
            normalizeText(status) === 'PRONTO'
              ? 'bg-emerald-500 shadow-emerald-500/30'
              : 'bg-orange-500 shadow-orange-500/30'
          }`}
        >
          {count}
        </div>
      </div>

      <div
        className="p-4 flex flex-col gap-3 max-h-[30rem] overflow-y-auto"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor:
            'rgba(128,128,128,0.15) transparent',
        }}
      >
        {!items.length ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <Icon
              name={
                normalizeText(status) === 'PRONTO'
                  ? 'PackageOpen'
                  : 'CheckCircle2'
              }
              size={30}
              className="text-muted-foreground/20"
            />

            <span className="text-[11px] font-black uppercase tracking-widest text-muted-foreground/40">
              {normalizeText(status) === 'PRONTO'
                ? 'Nessuna macchina pronta'
                : 'Nessuna macchina in riparazione'}
            </span>
          </div>
        ) : (
          items.map(item => (
            <MachinePill
              key={item.id}
              item={item}
              status={status}
              onOpen={onOpen}
            />
          ))
        )}
      </div>
    </div>
  );
};

const ScortaRaddrizzatriciModal = ({
  isOpen,
  onClose,
}) => {
  const [selectedColata, setSelectedColata] =
    useState('CCA');

  const [pronti, setPronti] = useState([]);
  const [inRiparazione, setInRiparazione] =
    useState([]);

  const [isLoading, setIsLoading] =
    useState(false);

  const [isExporting, setIsExporting] =
    useState(false);

  const [lastRefresh, setLastRefresh] =
    useState(null);

  const [errorMessage, setErrorMessage] =
    useState('');

  const [selectedItem, setSelectedItem] =
    useState(null);

  const fetchScorta = useCallback(async colata => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select(`
          id,
          n_macchina,
          stato,
          data_intervento,
          data_rientro,
          colata,
          linea,
          posizione,
          stato_rid,
          causa_freno,
          note,
          snrientrato,
          snsmontato,
          snmontato,
          tipointervento
        `)
        .eq('tipointervento', 'SMONTATO')
        .eq('colata', colata)
        .not('n_macchina', 'is', null)
        .not('data_intervento', 'is', null)
        .in('stato', [
          'PRONTO',
          'IN RIPARAZIONE',
        ])
        .order('data_intervento', {
          ascending: false,
        });

      if (error) {
        throw error;
      }

      /*
       * Per ogni macchina viene tenuto il record SMONTATO
       * più recente. La query è ordinata DESC.
       */
      const latestByMachine = {};

      for (const row of data || []) {
        const machine = normalizeValue(
          row.n_macchina
        );

        if (!machine || latestByMachine[machine]) {
          continue;
        }

        latestByMachine[machine] = {
          ...row,
          n_macchina: machine,
        };
      }

      const allMachines = Object.values(
        latestByMachine
      );

      const readyMachines = allMachines.filter(
        item =>
          normalizeText(item.stato) === 'PRONTO'
      );

      const repairMachines = allMachines.filter(
        item =>
          normalizeText(item.stato) ===
          'IN RIPARAZIONE'
      );

      const byMachineName = (first, second) =>
        first.n_macchina.localeCompare(
          second.n_macchina,
          'it',
          {
            numeric: true,
            sensitivity: 'base',
          }
        );

      setPronti(
        readyMachines.sort(byMachineName)
      );

      setInRiparazione(
        repairMachines.sort(byMachineName)
      );

      setLastRefresh(new Date());
    } catch (error) {
      console.error(
        'Errore scorta raddrizzatrici rd_cco:',
        error
      );

      setPronti([]);
      setInRiparazione([]);

      setErrorMessage(
        error?.message ||
          'Errore durante il caricamento della scorta.'
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    fetchScorta(selectedColata);

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [
    isOpen,
    selectedColata,
    fetchScorta,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handler = event => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    window.addEventListener(
      'keydown',
      handler
    );

    return () =>
      window.removeEventListener(
        'keydown',
        handler
      );
  }, [
    isOpen,
    onClose,
  ]);

  const allItems = useMemo(
    () => [
      ...pronti,
      ...inRiparazione,
    ],
    [
      pronti,
      inRiparazione,
    ]
  );

  const getDataForPdf = async colata => {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select(`
        id,
        n_macchina,
        stato,
        data_intervento,
        data_rientro,
        colata,
        linea,
        posizione,
        stato_rid,
        tipointervento
      `)
      .eq('tipointervento', 'SMONTATO')
      .eq('colata', colata)
      .not('n_macchina', 'is', null)
      .not('data_intervento', 'is', null)
      .in('stato', [
        'PRONTO',
        'IN RIPARAZIONE',
      ])
      .order('data_intervento', {
        ascending: false,
      });

    if (error) {
      throw error;
    }

    const latest = {};

    for (const row of data || []) {
      const machine = normalizeValue(
        row.n_macchina
      );

      if (machine && !latest[machine]) {
        latest[machine] = {
          ...row,
          n_macchina: machine,
        };
      }
    }

    return Object.values(latest);
  };

  const generatePDF = async () => {
    setIsExporting(true);

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      const printedAt =
        new Date().toLocaleString('it-IT');

      const allPages = await Promise.all(
        COLATE.map(getDataForPdf)
      );

      allPages.forEach((items, pageIndex) => {
        if (pageIndex) {
          doc.addPage();
        }

        const colata = COLATE[pageIndex];

        const colataTheme = getColataTheme(
          colata
        );

        const ready = items.filter(
          item =>
            normalizeText(item.stato) ===
            'PRONTO'
        );

        const repair = items.filter(
          item =>
            normalizeText(item.stato) ===
            'IN RIPARAZIONE'
        );

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);

        doc.text(
          'OrizonDesk — Gestione Scorte Raddrizzatrici',
          14,
          12
        );

        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);

        doc.text(
          `SCORTA RADDRIZZATRICI — ${colata}`,
          14,
          24
        );

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);

        doc.text(
          `Stato al: ${printedAt}`,
          14,
          31
        );

        doc.setDrawColor(...colataTheme.rgb);
        doc.setLineWidth(0.6);
        doc.line(14, 35, 283, 35);

        const createRows = (
          list,
          status
        ) =>
          list
            .sort((first, second) =>
              first.n_macchina.localeCompare(
                second.n_macchina,
                'it',
                {
                  numeric: true,
                  sensitivity: 'base',
                }
              )
            )
            .map(item => {
              const date = getReferenceDate(
                item,
                status
              );

              const days = daysSince(date);

              return [
                item.n_macchina,
                item.stato,
                item.linea || '---',
                item.posizione || '---',
                formatDate(date),
                days === 0
                  ? 'Oggi'
                  : days === null
                    ? '---'
                    : `${days} gg`,
              ];
            });

        const readyTheme =
          getStatusTheme('PRONTO');

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...readyTheme.pdfColor);

        doc.text(
          `MACCHINE PRONTE (${ready.length})`,
          14,
          44
        );

        autoTable(doc, {
          startY: 48,
          head: [[
            'N° MACCHINA',
            'STATO',
            'LINEA',
            'POSIZIONE',
            getPdfDateLabel('PRONTO'),
            'GIORNI',
          ]],
          body: createRows(
            ready,
            'PRONTO'
          ).length
            ? createRows(
                ready,
                'PRONTO'
              )
            : [[
                '---',
                'Nessuna macchina pronta',
                '---',
                '---',
                '---',
                '---',
              ]],
          theme: 'striped',
          headStyles: {
            fillColor: readyTheme.pdfColor,
            fontSize: 8,
            fontStyle: 'bold',
            halign: 'center',
          },
          styles: {
            fontSize: 8,
            cellPadding: 3,
          },
          columnStyles: {
            0: {
              fontStyle: 'bold',
            },
            1: {
              halign: 'center',
            },
            2: {
              halign: 'center',
            },
            3: {
              halign: 'center',
            },
            4: {
              halign: 'center',
            },
            5: {
              halign: 'center',
            },
          },
        });

        const repairY =
          doc.lastAutoTable.finalY + 13;

        const repairTheme =
          getStatusTheme(
            'IN RIPARAZIONE'
          );

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...repairTheme.pdfColor);

        doc.text(
          `IN RIPARAZIONE (${repair.length})`,
          14,
          repairY
        );

        autoTable(doc, {
          startY: repairY + 4,
          head: [[
            'N° MACCHINA',
            'STATO',
            'LINEA',
            'POSIZIONE',
            getPdfDateLabel(
              'IN RIPARAZIONE'
            ),
            'GIORNI',
          ]],
          body: createRows(
            repair,
            'IN RIPARAZIONE'
          ).length
            ? createRows(
                repair,
                'IN RIPARAZIONE'
              )
            : [[
                '---',
                'Nessuna macchina in riparazione',
                '---',
                '---',
                '---',
                '---',
              ]],
          theme: 'striped',
          headStyles: {
            fillColor: repairTheme.pdfColor,
            fontSize: 8,
            fontStyle: 'bold',
            halign: 'center',
          },
          styles: {
            fontSize: 8,
            cellPadding: 3,
          },
          columnStyles: {
            0: {
              fontStyle: 'bold',
            },
            1: {
              halign: 'center',
            },
            2: {
              halign: 'center',
            },
            3: {
              halign: 'center',
            },
            4: {
              halign: 'center',
            },
            5: {
              halign: 'center',
            },
          },
        });

        const page = doc.internal.pageSize;
        const width =
          page.width || page.getWidth();
        const height =
          page.height || page.getHeight();

        doc.setDrawColor(230);
        doc.line(
          14,
          height - 15,
          width - 14,
          height - 15
        );

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(150);

        doc.text(
          `Stampato il: ${printedAt}`,
          14,
          height - 10
        );

        doc.text(
          `OrizonDesk — Pagina ${pageIndex + 1}/2`,
          width / 2,
          height - 10,
          {
            align: 'center',
          }
        );

        doc.text(
          'https://desk.orizon-aix.com',
          width - 14,
          height - 10,
          {
            align: 'right',
          }
        );
      });

      doc.save(
        `Scorta_Raddrizzatrici_${new Date()
          .toISOString()
          .slice(0, 10)}.pdf`
      );
    } catch (error) {
      console.error(
        'Errore PDF scorta raddrizzatrici:',
        error
      );

      alert(
        'Errore durante la generazione del PDF.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const colataTheme = getColataTheme(
    selectedColata
  );

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 overflow-hidden">
        <button
          type="button"
          aria-label="Chiudi scorta raddrizzatrici"
          className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300"
          onClick={onClose}
        />

        <div className="relative w-full max-w-4xl bg-card border border-border/50 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-300">
          <div className="absolute -top-16 -right-16 w-48 h-48 bg-emerald-500/10 blur-[60px] rounded-full pointer-events-none" />

          <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-orange-500/10 blur-[60px] rounded-full pointer-events-none" />

          <div className="shrink-0 px-6 sm:px-8 py-5 sm:py-6 flex flex-wrap items-center justify-between gap-4 border-b border-border/40 relative z-10 bg-card/70 backdrop-blur-sm">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-11 h-11 rounded-2xl bg-muted/50 border border-border/30 flex items-center justify-center text-foreground/60 shadow-sm shrink-0">
                <Icon
                  name="PackageSearch"
                  size={21}
                />
              </div>

              <div className="min-w-0">
                <h2 className="text-lg font-black tracking-tighter uppercase text-foreground truncate">
                  Scorta Raddrizzatrici
                </h2>

                <p className="text-[9px] font-black uppercase tracking-[.25em] text-muted-foreground/50 mt-0.5 truncate">
                  Macchine SMONTATE — CCO
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <div className="flex p-1 rounded-xl bg-muted/60 border border-border/50">
                {COLATE.map(colata => {
                  const active =
                    selectedColata === colata;

                  const theme = getColataTheme(
                    colata
                  );

                  return (
                    <button
                      type="button"
                      key={colata}
                      onClick={() =>
                        setSelectedColata(colata)
                      }
                      className={`px-4 sm:px-5 py-2 rounded-lg sm:rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                        active
                          ? `${theme.active} shadow-lg`
                          : 'text-muted-foreground/60 hover:text-foreground'
                      }`}
                    >
                      {colata}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={generatePDF}
                disabled={
                  isLoading ||
                  isExporting ||
                  allItems.length === 0
                }
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-blue-500/10 hover:bg-blue-500 border border-blue-500/20 text-blue-500 hover:text-white transition-all disabled:opacity-40"
                title="Scarica PDF"
              >
                <Icon
                  name={
                    isExporting
                      ? 'Loader2'
                      : 'FileDown'
                  }
                  size={16}
                  className={
                    isExporting
                      ? 'animate-spin'
                      : ''
                  }
                />
              </button>

              <button
                type="button"
                onClick={() =>
                  fetchScorta(selectedColata)
                }
                disabled={isLoading}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-muted/50 hover:bg-muted border border-border/40 text-muted-foreground hover:text-foreground transition-all disabled:opacity-40"
                title="Aggiorna"
              >
                <Icon
                  name="RefreshCw"
                  size={14}
                  className={
                    isLoading
                      ? 'animate-spin'
                      : ''
                  }
                />
              </button>

              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-muted/50 hover:bg-red-500/10 hover:text-red-500 border border-border/40 hover:border-red-500/20 text-muted-foreground transition-all group"
                title="Chiudi"
              >
                <Icon
                  name="X"
                  size={16}
                  className="group-hover:rotate-90 transition-transform duration-200"
                />
              </button>
            </div>
          </div>

          <div className="shrink-0 px-6 sm:px-8 py-4 border-b border-border/30 relative z-10 bg-muted/10 flex flex-wrap items-center gap-3">
            <div
              className={`px-4 py-2 rounded-xl ${colataTheme.bg} border ${colataTheme.border} flex items-center gap-2`}
            >
              <span
                className={`w-2 h-2 rounded-full ${colataTheme.dot}`}
              />

              <span
                className={`text-[11px] font-black uppercase tracking-widest ${colataTheme.text}`}
              >
                {selectedColata}
              </span>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-2 text-[9px] font-black uppercase tracking-widest">
              <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                <i className="w-2 h-2 rounded-full bg-emerald-500" />
                Pronte
              </span>

              <span className="flex items-center gap-1.5 text-orange-500">
                <i className="w-2 h-2 rounded-full bg-orange-500" />
                In riparazione
              </span>
            </div>
          </div>

          <div
            className="flex-1 overflow-y-auto px-5 sm:px-8 py-6 flex flex-col gap-5 relative z-10"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor:
                'rgba(128,128,128,0.15) transparent',
            }}
          >
            {isLoading ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {[0, 1].map(item => (
                  <div
                    key={item}
                    className="rounded-3xl border border-border/30 overflow-hidden animate-pulse"
                  >
                    <div className="px-6 py-5 bg-muted/20 flex justify-between">
                      <div className="flex gap-3">
                        <div className="w-9 h-9 rounded-xl bg-muted/50" />

                        <div className="flex flex-col gap-2">
                          <div className="w-28 h-3 rounded bg-muted/50" />
                          <div className="w-20 h-2 rounded bg-muted/40" />
                        </div>
                      </div>

                      <div className="w-10 h-10 rounded-2xl bg-muted/50" />
                    </div>

                    <div className="p-4 flex flex-col gap-3">
                      {[0, 1, 2].map(row => (
                        <div
                          key={row}
                          className="h-36 rounded-2xl bg-muted/30"
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : errorMessage ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <Icon
                  name="TriangleAlert"
                  size={38}
                  className="text-red-500/60"
                />

                <span className="max-w-md text-center text-sm font-bold text-red-500/70">
                  {errorMessage}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    fetchScorta(selectedColata)
                  }
                  className="px-5 py-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary text-[10px] font-black uppercase tracking-widest"
                >
                  Riprova
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Section
                  title="Macchine Pronte"
                  icon="CheckCircle2"
                  count={pronti.length}
                  items={pronti}
                  status="PRONTO"
                  onOpen={item =>
                    setSelectedItem({
                      item,
                      status: 'PRONTO',
                    })
                  }
                />

                <Section
                  title="In Riparazione"
                  icon="Wrench"
                  count={inRiparazione.length}
                  items={inRiparazione}
                  status="IN RIPARAZIONE"
                  onOpen={item =>
                    setSelectedItem({
                      item,
                      status: 'IN RIPARAZIONE',
                    })
                  }
                />
              </div>
            )}
          </div>

          <div className="shrink-0 px-6 sm:px-8 py-4 bg-muted/20 border-t border-border/40 flex flex-wrap items-center justify-between gap-3 relative z-10">
            <div className="flex items-center gap-2 text-muted-foreground/30">
              <Icon name="Clock" size={11} />

              <span className="text-[9px] font-black uppercase tracking-[.2em]">
                {lastRefresh
                  ? `Aggiornato ${lastRefresh.toLocaleTimeString(
                      'it-IT',
                      {
                        hour: '2-digit',
                        minute: '2-digit',
                      }
                    )}`
                  : 'Caricamento...'}
              </span>
            </div>

            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5 text-[10px] font-black">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />

                <span className="text-emerald-600 dark:text-emerald-400 tabular-nums">
                  {pronti.length}
                </span>

                <span className="text-muted-foreground/40 uppercase tracking-widest">
                  pronte
                </span>
              </span>

              <span className="flex items-center gap-1.5 text-[10px] font-black">
                <span className="w-2 h-2 rounded-full bg-orange-500" />

                <span className="text-orange-500 tabular-nums">
                  {inRiparazione.length}
                </span>

                <span className="text-muted-foreground/40 uppercase tracking-widest">
                  rip.
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <StockDetailModal
        item={selectedItem?.item}
        status={selectedItem?.status}
        onClose={() => setSelectedItem(null)}
      />
    </>
  );
};

export default ScortaRaddrizzatriciModal;